"use client";
import { IBaseLabelValue, TPayFines } from "@/interfaces";
import {
  Accordion,
  CustomButton,
  FlexBetween,
  FlexCenter,
  FlexCol,
} from "@/components";
import { cn, formatNumberWithDots, generatePaymentCallbackUrl } from "@/lib";
import { useTranslations } from "next-intl";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import InputDataContent from "@/components/inquiryDetails/inputDataContent";
import ResultContent from "@/components/resultContent";
import React, { useEffect, useRef, useState } from "react";
import { routes } from "@/routes";
import { useToastStore } from "@/store/toastStore";
import DownloadPdf, { DownloadPdfRef } from "../../pdfTemplates/downloadPdf";
import { usePayFinesMutation, usePurchaseFileMutation } from "@/hooks";
import { useSession } from "next-auth/react";
import { useAppStore, useModalStore } from "@/store";
import PdfTemplate from "@/components/pdfTemplates/pdfTemplate";
import VehiclesPaymentModal from "@/components/Payment/vehiclesPayment/vehiclePaymentModal";
import {
  IPayFinesPayload,
  IRawPurchasedMotorcycleFinesResData,
} from "@/interfaces/inquiries/vehicle";

const MotorcycleFinesInquiryDetails = ({
  data,
}: {
  data: IRawPurchasedMotorcycleFinesResData;
}) => {
  const { data: session } = useSession();
  const searchParams = useSearchParams();
  const t = useTranslations("serviceResponseDetails");
  const router = useRouter();
  const [openAccordionId, setOpenAccordionId] = useState<string | null>(null);
  const {
    isPending: isPaySubmitting,
    mutate: PayFineFn,
    error: paySubmittingError,
    reset: resetPay,
  } = usePayFinesMutation(session?.user.token);
  const trafficFinesData = data.data.data.body;
  const servicePdfFee = (data as { inquery?: { pdf_fee?: string | number | null } })
    .inquery?.pdf_fee;
  const pathName = usePathname();
  const { setPreviousPath } = useAppStore();
  const inputContent: IBaseLabelValue[] = [
    {
      label: t("fines.plateChar"),
      value: trafficFinesData.plateChar,
    },
  ];

  const resultContent: IBaseLabelValue[] = [
    {
      label: t("fines.complaint"),
      value: trafficFinesData.complaint,
    },
    {
      label: t("fines.parmDate"),
      value: `${trafficFinesData.sysDate} - ${trafficFinesData.sysTime}`,
    },
    {
      label: t("fines.traceId"),
      value: trafficFinesData.traceId,
    },
  ];

  const finesItemResultContent: IBaseLabelValue[] = [
    {
      label: t("fines.warningDTOs.serialNo"),
      value: "serialNo",
    },
    {
      label: t("fines.warningDTOs.warningId"),
      value: "warningId",
    },
    {
      label: t("fines.warningDTOs.violationDeliveryType"),
      value: "violationDeliveryType",
    },
    {
      label: t("fines.warningDTOs.violatoinAddress"),
      value: "violatoinAddress",
    },
    {
      label: t("fines.warningDTOs.violationTypeDTO"),
      value: "violationTypeDTO",
    },
    {
      label: t("fines.warningDTOs.violationOccureDate"),
      value: "violationOccureDate",
    },
    {
      label: t("fines.warningDTOs.paperId"),
      value: "paperId",
    },
    {
      label: t("fines.warningDTOs.paymentId"),
      value: "paymentId",
    },
    {
      label: t("fines.warningDTOs.hasImage"),
      value: "hasImage",
    },
  ];
  const getWarningValue = (warning: any, key: string | number) => {
    switch (key) {
      case "violationTypeDTO":
        return warning.violationTypeDTO.violationType;
      case "violationDeliveryType":
        return warning.violationDeliveryType.violationDeliveryType;
      case "hasImage":
        return warning.hasImage === "1"
          ? t("fines.warningDTOs.yes")
          : t("fines.warningDTOs.no");
      default:
        return warning[key] ?? "-";
    }
  };
  const { setOpenToast } = useToastStore();

  const buildPdfTableData = () => {
    const tableRows = resultContent.map((item) => [item.label, item.value]);

    tableRows.push(["", ""]);

    trafficFinesData.warningDTOs.forEach((warning, idx) => {
      tableRows.push([`جریمه شماره ${idx + 1}`, ""]);

      finesItemResultContent.forEach((item) => {
        tableRows.push([item.label, getWarningValue(warning, item.value)]);
      });

      tableRows.push(["", ""]);
    });

    return tableRows;
  };

  const handlePayFineSubmission = (fine: TPayFines) => {
    const payload: IPayFinesPayload = {
      amount: fine.inquirePrice,
      callback_url: `${generatePaymentCallbackUrl(pathName)}?service=fines`,
      bill_id: Number(fine.paperId),
      pay_id: Number(fine.paymentId),
    };

    PayFineFn(
      { payload },
      {
        onSuccess: (data) => {
          if (data.data) {
            setPreviousPath(pathName);
            window.location.href = data.data?.payment_link;
          }
        },
      },
    );
  };
  const pdfContentRef = useRef<HTMLDivElement>(null);
  const downloadPdfRef = useRef<DownloadPdfRef>(null);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [timerEndTime, setTimerEndTime] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { openModal, closeModal, setOpenModal } = useModalStore();
  const {
    isPending: isFileSubmitting,
    mutate: purchaseFileFn,
    error: purchaseFileError,
    reset: resetPurchaseFile,
  } = usePurchaseFileMutation(session?.user.token);
  const handleTimerComplete = () => {
    setIsTimerActive(false);
    setTimerEndTime(null);
  };
  const handleCloseModal = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("download");
    url.searchParams.delete("payment");
    window.history.replaceState({}, "", url.toString());
    closeModal();
  };
  const downloadPaymentSubmission = () => {
    purchaseFileFn(
      { payload: { purchase_id: data.id } },
      {
        onSuccess: (data) => {
          if (data.success) {
            setOpenToast({
              variant: "success",
              message: "خرید با موفقیت انجام شد",
              position: "top-center",
            });
            setTimeout(() => {
              // setOpenToast({
              //   variant: "success",
              //   message: "شروع دانلود فایل",
              //   position: "top-center",
              // });
              downloadPdfRef.current?.download();
            }, 1000);
            handleCloseModal();
          }
          setError(null);
          setIsTimerActive(false);
          setTimerEndTime(null);
        },
        onError: (error) => {
          if (error) {
            setError(error.message);
            setIsTimerActive(true); // Start timer
            setTimerEndTime(Date.now() + 60000);
          }
        },
      },
    );
  };
  useEffect(() => {
    const shouldDownload = searchParams?.get("download");
    if (shouldDownload) {
      setOpenModal("vehicle-payment-method");
      // if (shouldDownload === "true") {
      //   // Small delay to ensure component is fully mounted
      //   setTimeout(() => {
      //     downloadPaymentSubmission();
      //   }, 500);
      // } else {
      //   setOpenModal("vehicle-payment-method");
      // }
    }
  }, [searchParams]);
  return (
    <div className="shadow-main from-sky-bliss relative basis-[500px] rounded-2xl bg-linear-to-b to-transparent p-[2px] lg:basis-[640px]">
      <FlexCol className="items-stretch rounded-2xl bg-white p-8">
        <h2 className="mb-5 text-center font-bold lg:text-lg">
          {t("motorcycleFines.title")}
        </h2>
        <InputDataContent
          content={inputContent}
          className="le p-6 md:p-8"
          itemClassName="flex-col md:flex-row leading-9 md:leading-none"
        />
        <FlexBetween className="mb-3 items-center">
          <h2 className="text-center text-lg font-bold">
            {t("accountToIban.resultTitle")}
          </h2>
          <DownloadPdf
            ref={downloadPdfRef}
            className="hidden"
            id="motor-fines-inquiry-download-pdf"
            resultContent={buildPdfTableData()}
            fileName={`خلافی-موتورسیکلت-${trafficFinesData.plateChar}`}
            tableTitle="خلافی موتورسیکلت"
            useHtmlToPdf={true}
            htmlContentRef={pdfContentRef}
          />
        </FlexBetween>
        <ResultContent content={resultContent} className="md:gap-4" />
        {trafficFinesData.warningDTOs.length > 0 &&
        trafficFinesData.inquirePrice !== "0" ? (
          <>
            <div className="mb-3 flex items-center text-sm md:text-base">
              <span>{t("fines.warningDTOs.count")}:</span>
              <span className="mx-1">
                {trafficFinesData.warningDTOs.length} عدد
              </span>
            </div>
            <div className="max-h-[500px] overflow-x-hidden overflow-y-auto">
              {trafficFinesData.warningDTOs.map((warning, i) => (
                <Accordion
                  key={i}
                  title={`${formatNumberWithDots(warning.finalPrice)} ریال `}
                  open={warning.warningId === openAccordionId}
                  hasDetails
                  className="mb-2"
                  onOpenChange={(isOpen) => {
                    setOpenAccordionId(isOpen ? warning.warningId : null);
                  }}
                  details={
                    <>
                      <p className="text-sm">
                        {t("fines.warningDTOs.violationOccureDate")}
                      </p>
                      <p className="text-sm" dir="ltr">
                        {warning.violationOccureDate}
                      </p>
                    </>
                  }
                >
                  <div
                    className={cn(
                      "shadow-main mb-10 flex flex-col gap-4 rounded-xl p-4",
                    )}
                  >
                    <ul className="flex flex-col gap-4">
                      {finesItemResultContent.map((item, index) => (
                        <li
                          key={index}
                          className="flex-between flex items-start gap-6 text-xs md:gap-8 md:text-sm"
                        >
                          <span className="min-w-max">{item.label}</span>
                          <div className="text-primary-main flex items-center gap-2 text-start">
                            {getWarningValue(warning, item.value)}
                          </div>
                        </li>
                      ))}
                    </ul>
                    {warning.finalPrice !== "0" && (
                      <CustomButton
                        onClick={() =>
                          handlePayFineSubmission({
                            paperId: Number(warning.paperId),
                            paymentId: Number(warning.paymentId),
                            inquirePrice: warning.finalPrice,
                          })
                        }
                        variant="outline"
                        className={cn(
                          "text-success-main border-success-main hover:shadow-main hover:text-success-main mx-auto w-max px-2",
                        )}
                      >
                        <span className="text-sm">
                          {t("fines.warningDTOs.payThisFine")}
                        </span>
                      </CustomButton>
                    )}
                  </div>
                </Accordion>
              ))}
            </div>
            <h2 className="mt-6 text-center font-bold lg:text-lg">
              پرداخت خلافی
            </h2>
            <div className="shadow-main mt-6 rounded-xl p-6 md:p-8">
              <div className="text-sm md:text-base">
                <ul className="flex flex-col gap-4">
                  <li className="flex-between flex items-start gap-6 text-xs sm:text-sm md:gap-8 md:text-sm lg:text-base">
                    <span className="min-w-max">{t("fines.paperId")}</span>
                    <div className="text-primary-main flex items-center gap-2 text-start">
                      {trafficFinesData.paperId}
                    </div>
                  </li>
                  <li className="flex-between flex items-start gap-6 text-xs sm:text-sm md:gap-8 lg:text-base">
                    <span className="min-w-max">{t("fines.paymentId")}</span>
                    <div className="text-primary-main flex items-center gap-2 text-start">
                      {trafficFinesData.paymentId}
                    </div>
                  </li>
                  <li className="flex-between flex items-start gap-6 text-xs sm:text-sm md:gap-8 lg:text-base">
                    <span className="min-w-max">
                      {t("fines.warningDTOs.totalPrice")}
                    </span>
                    <div className="text-primary-main flex items-center gap-2 text-start">
                      {formatNumberWithDots(trafficFinesData.inquirePrice)} ریال
                    </div>
                  </li>
                </ul>
              </div>
              <div className="pt-10 md:mt-0">
                {trafficFinesData.inquirePrice !== "0" && (
                  <CustomButton
                    onClick={() =>
                      handlePayFineSubmission({
                        paperId: Number(trafficFinesData.paperId),
                        paymentId: Number(trafficFinesData.paymentId),
                        inquirePrice: trafficFinesData.inquirePrice,
                      })
                    }
                    className={cn(
                      "bg-success-main mx-auto w-max text-white hover:text-white",
                    )}
                  >
                    {t("fines.warningDTOs.payment")}
                  </CustomButton>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="bg-success-main rounded-lg py-4 text-center text-sm text-white md:text-base">
            این پلاک فاقد جریمه است.
          </div>
        )}
        <FlexCenter className="mt-10 flex flex-col justify-center md:flex-row md:gap-6">
          <CustomButton
            id="details-khalafi-inquiry-download"
            className="order-2 flex w-fit cursor-pointer items-center justify-center rounded-md border-none! bg-red-700 py-[6px] text-white transition-all hover:bg-red-800 md:order-1 lg:rounded-lg lg:py-[8px]"
            onClick={() => setOpenModal("vehicle-payment-method")}
          >
            دانلود نتیجه
          </CustomButton>
          <div className="order-1 mb-4 flex gap-4 md:order-2 md:m-0 xl:gap-6">
            <CustomButton
              id="details-motor-fines-inquiry-back"
              className={cn("w-fit bg-gray-500")}
              onClick={() =>
                router.push(routes.main.INQUIRY_HISTORY.primaryPath)
              }
            >
              {t("companyInformationInquiry.backButton")}
            </CustomButton>
            <CustomButton
              id="details-motor-fines-inquiry-new-request"
              onClick={() =>
                router.push(routes.services.Motorcycle_Fines.rewritePath)
              }
              className={cn("bg-primary-main mx-auto w-fit px-8! text-white")}
            >
              {t("newRequest")}
            </CustomButton>
          </div>
        </FlexCenter>
      </FlexCol>
      {/* This div will be used for PDF generation - DetailCreditCard style */}
      <div
        ref={pdfContentRef}
        style={{
          position: "fixed",
          left: "-9999px",
          top: "-9999px",
          width: "800px",
          fontFamily: "IRANYekan, sans-serif",
          margin: "2rem auto",
        }}
      >
        <PdfTemplate
          tableTitle="گزارش خلافی موتورسیکلت"
          resultContent={buildPdfTableData()}
          code={data.code}
          dateTime={data.created_at}
        />
      </div>
      {openModal === "vehicle-payment-method" && (
        <VehiclesPaymentModal
          payBtnId="payment-file-submit"
          submissionError={purchaseFileError}
          paymentSubmission={downloadPaymentSubmission}
          serviceCost={servicePdfFee}
          onClose={handleCloseModal}
          isOpen={openModal === "vehicle-payment-method"}
          submissionLoading={isFileSubmitting}
          onErrorReset={resetPurchaseFile}
          showManualError={error}
          isTimerActive={isTimerActive}
          timerEndTime={timerEndTime}
          onTimerComplete={handleTimerComplete}
        />
      )}
    </div>
  );
};

export default MotorcycleFinesInquiryDetails;
